/**
 * Las claves de caché, en un solo lugar.
 *
 * ## Qué reemplazan
 *
 * En Next, después de cada mutación se llamaba a `revalidatePath("/inbox")` y
 * el framework se encargaba. Acá el equivalente es
 * `queryClient.invalidateQueries({ queryKey: keys.inbox.all })`.
 *
 * La diferencia que importa: `revalidatePath` no falla nunca aunque la ruta
 * esté mal escrita, y una clave equivocada tampoco. **El síntoma de los dos es
 * el mismo —dato viejo en pantalla— y no rompe el build.** Por eso las claves
 * viven acá y no escritas a mano en cada componente: un solo lugar donde
 * mirar cuando algo no se refresca.
 *
 * ## Cómo están armadas
 *
 * Jerárquicas y de lo general a lo específico, porque TanStack Query invalida
 * **por prefijo**: `keys.agents.all` alcanza a la lista, al detalle de cada
 * agente y a sus versiones de una sola vez. Es lo que traduce el
 * `revalidatePath("/agents", "layout")` del código anterior, que invalidaba el
 * árbol entero.
 */
import type { ContactQuery, InboxQuery, InquiryKind, PropertyQuery } from "@/lib/api/types";

export const keys = {
  session: ["session"] as const,
  navigation: ["navigation"] as const,
  mcpTokens: () => ["mcp", "tokens"] as const,
  /** Estado del sync con el CRM del cliente. */
  crmSync: ["crm-sync"] as const,
  /** El rubro del espacio: decide qué módulos y qué campos se muestran. */
  // La bienvenida: una por espacio, así que no lleva parámetros.
  onboarding: ["onboarding"] as const,
  profile: ["profile"] as const,

  agents: {
    all: ["agents"] as const,
    list: () => ["agents", "list"] as const,
    detail: (agentId: number) => ["agents", "detail", agentId] as const,
    versions: (agentId: number) => ["agents", "versions", agentId] as const,
    /** Lo que está contestando hoy. Contra esto se calcula «qué cambió». */
    published: (agentId: number) => ["agents", "published", agentId] as const,
    /* Bajo `agents`: publicar invalida `keys.agents.all` y con eso se entera de
     * que ya no hay herramientas pendientes de publicar. */
    externalTools: (agentId: number) => ["agents", "external-tools", agentId] as const,
  },

  channels: {
    all: ["channels"] as const,
    list: () => ["channels", "list"] as const,
    // Del servidor y no del canal: es la misma app de Meta para todos.
    signupConfig: () => ["channels", "signup-config"] as const,
    // Por canal: dos canales de WhatsApp tienen plantillas distintas, y
    // compartir la clave haría que el segundo mostrara las del primero.
    standardTemplates: (channelId: number) =>
      ["channels", "templates", "standard", channelId] as const,
    templates: (channelId: number) => ["channels", "templates", channelId] as const,
  },

  inbox: {
    unread: () => ["inbox", "unread"] as const,
    all: ["inbox"] as const,
    conversations: (query: InboxQuery) => ["inbox", "conversations", query] as const,
    // Por embudo: las etapas de propietarios no son las de compradores.
    meta: (kind: InquiryKind = "buyer") => ["inbox", "meta", kind] as const,
    conversation: (conversationId: number) =>
      ["inbox", "conversation", conversationId] as const,
    turns: (conversationId: number) => ["inbox", "turns", conversationId] as const,
    events: (conversationId: number) => ["inbox", "events", conversationId] as const,
    notes: (conversationId: number) => ["inbox", "notes", conversationId] as const,
    editableFields: () => ["inbox", "editable-fields"] as const,
  },

  pipeline: {
    all: ["pipeline"] as const,
    config: () => ["pipeline", "config"] as const,
    stages: (kind: "buyer" | "seller") => ["pipeline", "stages", kind] as const,
  },

  contacts: {
    all: ["contacts"] as const,
    list: (query: ContactQuery) => ["contacts", "list", query] as const,
    counts: () => ["contacts", "counts"] as const,
    detail: (contactId: number) => ["contacts", "detail", contactId] as const,
    inquiries: (contactId: number) => ["contacts", "inquiries", contactId] as const,
  },

  agenda: {
    all: ["agenda"] as const,
    summary: () => ["agenda", "summary"] as const,
    visits: (params: { conversationId?: number; onlyOpen?: boolean }) =>
      ["agenda", "visits", params] as const,
    tasks: (params: { conversationId?: number; includeCompleted?: boolean }) =>
      ["agenda", "tasks", params] as const,
  },

  calendar: {
    all: ["calendar"] as const,
    status: () => ["calendar", "status"] as const,
    events: (days: number) => ["calendar", "events", days] as const,
  },

  properties: {
    all: ["properties"] as const,
    search: (query: PropertyQuery) => ["properties", "search", query] as const,
    facets: () => ["properties", "facets"] as const,
  },

  analytics: {
    all: ["analytics"] as const,
    overview: (days: number) => ["analytics", "overview", days] as const,
    investments: () => ["analytics", "investments"] as const,
  },

  segments: {
    all: ["segments"] as const,
    list: () => ["segments", "list"] as const,
    detail: (segmentId: number) => ["segments", "detail", segmentId] as const,
    vocabulary: () => ["segments", "vocabulary"] as const,
  },

  campaigns: {
    all: ["campaigns"] as const,
    list: () => ["campaigns", "list"] as const,
    detail: (campaignId: number) => ["campaigns", "detail", campaignId] as const,
    recipients: (campaignId: number) => ["campaigns", "recipients", campaignId] as const,
  },

  automations: {
    all: ["automations"] as const,
    list: () => ["automations", "list"] as const,
    history: (automationId: number) => ["automations", "history", automationId] as const,
  },

  assignment: {
    all: ["assignment"] as const,
    rule: () => ["assignment", "rule"] as const,
  },

  usage: () => ["usage"] as const,

  settings: {
    all: ["settings"] as const,
    dashboard: () => ["settings", "dashboard"] as const,
    reminders: () => ["settings", "reminders"] as const,
    handoff: () => ["settings", "handoff"] as const,
    intent: () => ["settings", "intent"] as const,
    leadSources: () => ["settings", "lead-sources"] as const,
    sites: () => ["settings", "sites"] as const,
    sitesDetected: () => ["settings", "sites", "detected"] as const,
    approvedTemplates: () => ["settings", "handoff", "templates"] as const,
    inboundEmail: () => ["settings", "inbound-email"] as const,
    // Bajo `inboundEmail`: regenerar la dirección refresca también la lista.
    inboundEmails: () => ["settings", "inbound-email", "messages"] as const,
  },

  documents: {
    all: ["documents"] as const,
    list: (agentId?: number) => ["documents", "list", agentId ?? null] as const,
    formats: () => ["documents", "formats"] as const,
  },
} as const;
